import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from 'react'
import Button from '@/shared/Buttons/Button'
import DropArea from '@/shared/Files/Droparea'
import DropdownInput from '@/shared/Inputs/DropdownInput'
import FormField from '@/shared/Forms/FormField'
import Image from '@/shared/Common/Image'
import TextInput from '@/shared/Inputs/TextInput'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import SidePage from '@/shared/SidePage'
import alertService from '@/services/alerts/alert.service'
import CloseIcon from '@/assets/icons/close.svg?react'
import TrashIcon from '@/assets/icons/trash.svg?react'
import UploadIcon from '@/assets/icons/upload.svg?react'
import cmsApi from '../cms.api'
import { IGalleryCategory, IGalleryImage } from '../cms.types'
import css from '../cms.module.css'

interface IProps {
  open: boolean
  categories: IGalleryCategory[]
  images: IGalleryImage[]
  initialCategoryId?: number
  onClose: () => void
  onUploaded: () => Promise<void>
  onAssetsRefresh: () => Promise<void>
}

interface IPendingImage {
  clientId: string
  file: File
  previewUrl: string
  title: string
  orderingIndex: number
}

const GalleryImagesBatchUpload = (props: IProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingRef = useRef<IPendingImage[]>([])
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [pendingImages, setPendingImages] = useState<IPendingImage[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [saving, setSaving] = useState(false)
  const [progress, setProgress] = useState('')

  const categoryOptions = useMemo(
    () => props.categories.map((category) => ({ value: category.id, title: category.title })),
    [props.categories]
  )

  useEffect(() => {
    if (!props.open) return

    const initialCategoryId =
      props.initialCategoryId && props.categories.some((category) => category.id === props.initialCategoryId)
        ? props.initialCategoryId
        : undefined

    setCategoryId((prev) =>
      props.categories.some((category) => category.id === prev) ? prev : initialCategoryId ?? props.categories[0]?.id ?? ''
    )
  }, [props.open, props.categories, props.initialCategoryId])

  useEffect(() => {
    return () => {
      revokePreviews(pendingRef.current)
    }
  }, [])

  const updatePendingImages = (updater: (items: IPendingImage[]) => IPendingImage[]) => {
    setPendingImages((prev) => {
      const next = updater(prev)
      pendingRef.current = next
      return next
    })
  }

  const clearPendingImages = () => {
    revokePreviews(pendingRef.current)
    pendingRef.current = []
    setPendingImages([])
  }

  const close = () => {
    if (saving) return

    clearPendingImages()
    setDragActive(false)
    setProgress('')
    props.onClose()
  }

  const addFiles = (fileList?: FileList | null) => {
    if (!fileList) return

    const files = Array.from(fileList)
    const imageFiles = files.filter((file) => file.type.startsWith('image/'))

    if (imageFiles.length !== files.length) {
      alertService.requestAlert({
        type: 'warning',
        title: 'Часть файлов пропущена',
        description: 'Можно загрузить только изображения',
      })
    }

    if (!imageFiles.length) return

    const startOrderingIndex = getNextOrderingIndex(categoryId, props.images) + pendingRef.current.length
    const nextImages = imageFiles.map((file, index) => ({
      clientId: `${file.name}-${file.lastModified}-${file.size}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
      title: getDefaultAltText(file.name),
      orderingIndex: startOrderingIndex + index,
    }))

    updatePendingImages((prev) => [...prev, ...nextImages])
  }

  const changePendingImage = (clientId: string, field: 'title' | 'orderingIndex', value: string) => {
    updatePendingImages((prev) =>
      prev.map((item) =>
        item.clientId === clientId
          ? {
              ...item,
              [field]: field === 'orderingIndex' ? Math.max(0, Number(value || 0)) : value,
            }
          : item
      )
    )
  }

  const selectCategory = (value: number | string) => {
    const nextCategoryId = Number(value)
    const startOrderingIndex = getNextOrderingIndex(nextCategoryId, props.images)

    setCategoryId(nextCategoryId)
    updatePendingImages((prev) =>
      prev.map((item, index) => ({
        ...item,
        orderingIndex: startOrderingIndex + index,
      }))
    )
  }

  const removePendingImage = (clientId: string) => {
    updatePendingImages((prev) => {
      const removed = prev.find((item) => item.clientId === clientId)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return prev.filter((item) => item.clientId !== clientId)
    })
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files)
    event.target.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragActive(false)
    addFiles(event.dataTransfer.files)
  }

  const save = async () => {
    if (!categoryId) {
      alertService.requestAlert({ type: 'warning', title: 'Выберите категорию' })
      return
    }

    if (!pendingImages.length) {
      alertService.requestAlert({ type: 'warning', title: 'Выберите изображения' })
      return
    }

    setSaving(true)
    const createdIds: string[] = []
    const errors: string[] = []

    for (const [index, pendingImage] of pendingImages.entries()) {
      setProgress(`${index + 1}/${pendingImages.length}: ${pendingImage.file.name}`)

      const assetResponse = await cmsApi.uploadAsset(pendingImage.file)
      if (assetResponse.status === 'error') {
        errors.push(`${pendingImage.file.name}: ${assetResponse.message}`)
        continue
      }

      const imageResponse = await cmsApi.create<IGalleryImage>('gallery/images', {
        categoryId,
        title: pendingImage.title.trim() || getDefaultAltText(pendingImage.file.name),
        url: assetResponse.body.url,
        orderingIndex: Number(pendingImage.orderingIndex || 0),
      })

      if (imageResponse.status === 'error') {
        errors.push(`${pendingImage.file.name}: ${imageResponse.message}`)
        continue
      }

      createdIds.push(pendingImage.clientId)
    }

    await props.onAssetsRefresh()
    if (createdIds.length) await props.onUploaded()

    updatePendingImages((prev) => {
      const created = new Set(createdIds)
      prev.forEach((item) => {
        if (created.has(item.clientId)) URL.revokeObjectURL(item.previewUrl)
      })
      return prev.filter((item) => !created.has(item.clientId))
    })

    setSaving(false)
    setProgress('')

    if (!errors.length) {
      alertService.requestAlert({
        type: 'success',
        title: 'Изображения загружены',
        description: `${createdIds.length} изображений`,
      })
      close()
      return
    }

    alertService.requestAlert({
      type: createdIds.length ? 'warning' : 'error',
      title: createdIds.length ? 'Часть изображений не загружена' : 'Не удалось загрузить изображения',
      description: errors.slice(0, 3).join('\n'),
    })
  }

  return (
    <SidePage open={props.open} onClose={close} size="large">
      <div className={css.sidePageContent}>
        <h2 className="text-heading-4">Загрузить изображения</h2>
        <div className={css.formGrid}>
          <FormField label="Категория" required>
            <DropdownInput
              options={categoryOptions}
              selectedOption={categoryId}
              title={props.categories.length ? 'Выберите категорию' : 'Нет категорий'}
              onSelect={selectCategory}
              disabled={saving || props.categories.length === 0}
            />
          </FormField>

          <DropArea
            isActive={dragActive}
            onDrop={handleDrop}
            onDragEnter={(event) => {
              event.preventDefault()
              setDragActive(true)
            }}
            onDragLeave={() => setDragActive(false)}
            onDragOver={(event) => event.preventDefault()}
          >
            <div className={css.batchUploadDropzone} data-disabled={saving || props.categories.length === 0}>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleInputChange}
                disabled={saving || props.categories.length === 0}
              />
              <div>
                <p className="text-nm-bold">Изображения</p>
                <p className="text-sm text-description">{pendingImages.length} выбрано</p>
              </div>
              <Button
                size="default"
                type="default"
                text="Выбрать"
                icon={<UploadIcon />}
                disabled={saving || props.categories.length === 0}
                onClick={() => inputRef.current?.click()}
              />
            </div>
          </DropArea>

          {progress && (
            <div className="inline-flex-gap gap8 center">
              <ThreeDotsLoader />
              <p className="text-nm text-description">{progress}</p>
            </div>
          )}

          {pendingImages.length > 0 && (
            <div className={css.batchUploadList}>
              {pendingImages.map((item) => (
                <article key={item.clientId} className={css.batchUploadItem}>
                  <Image
                    alt={item.title}
                    src={item.previewUrl}
                    wrapperClassname={css.batchUploadPreview}
                    style={{ width: '100%', height: '100%' }}
                  />
                  <div className={css.batchUploadFields}>
                    <TextInput
                      name="title"
                      value={item.title}
                      onChange={(value) => changePendingImage(item.clientId, 'title', value)}
                      placeHolder="Alt текст"
                      disabled={saving}
                    />
                    <TextInput
                      name="orderingIndex"
                      value={String(item.orderingIndex)}
                      onChange={(value) => changePendingImage(item.clientId, 'orderingIndex', value)}
                      type="number"
                      placeHolder="Порядок"
                      disabled={saving}
                    />
                  </div>
                  <Button
                    size="default"
                    type="default"
                    danger
                    icon={<TrashIcon />}
                    disabled={saving}
                    onClick={() => removePendingImage(item.clientId)}
                  />
                </article>
              ))}
            </div>
          )}

          <div className={css.formFooter}>
            <Button size="default" type="default" text="Отмена" onClick={close} disabled={saving} />
            {pendingImages.length > 0 && (
              <Button
                size="default"
                type="default"
                text="Очистить"
                icon={<CloseIcon />}
                onClick={clearPendingImages}
                disabled={saving}
              />
            )}
            <Button
              size="default"
              type="primary"
              text="Загрузить"
              loading={saving}
              disabled={props.categories.length === 0}
              onClick={save}
            />
          </div>
        </div>
      </div>
    </SidePage>
  )
}

const getDefaultAltText = (fileName: string) => {
  return fileName.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' ').trim()
}

const getNextOrderingIndex = (categoryId: number | '', images: IGalleryImage[]) => {
  if (!categoryId) return 1

  const maxOrderingIndex = images
    .filter((image) => image.categoryId === categoryId)
    .reduce((max, image) => Math.max(max, Number(image.orderingIndex || 0)), 0)

  return maxOrderingIndex + 1
}

const revokePreviews = (items: IPendingImage[]) => {
  items.forEach((item) => URL.revokeObjectURL(item.previewUrl))
}

export default GalleryImagesBatchUpload
